// A text custom field's popover body on the Opportunities list. Update or Enter saves; Cancel discards,
// and Escape discards too because it is the popover's own dismiss. The value is trimmed, since surrounding
// spaces mean nothing in a one-line field, so saving an empty or spaces-only box clears the value.
import React, {useRef, useState} from 'react';
import {Button, EMPHASIS, TextField} from '@forecast-it/design-system';
import {Actions, Body} from './editorLayout';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

export const TextFieldEditor = ({node, def, current, onCommit, close, labels}: CustomFieldEditorProps): JSX.Element => {
	const initial = current ?? '';
	const [draft, setDraft] = useState(initial);
	const trimmed = draft.trim();
	// Both sides are trimmed: a value stored elsewhere with surrounding spaces is unchanged until edited.
	const unchanged = trimmed === initial.trim();
	// The popover stays mounted through its exit animation, so a second press could otherwise save again.
	const saving = useRef(false);
	const save = () => {
		if (unchanged || saving.current) return;
		saving.current = true;
		saveCustomFieldValue({node, def, value: trimmed === '' ? null : trimmed, onCommit});
		close();
	};
	// The design-system TextField does not forward onKeyDown, so Enter is caught as it bubbles out of the
	// input instead. Only the input's own Enter saves: Enter on a focused button presses that button, and
	// Enter that confirms an IME composition (Japanese, Chinese) is part of typing, not a save.
	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.target instanceof HTMLInputElement && !e.nativeEvent.isComposing) save();
	};
	return (
		<Body data-cy="cf-text-editor" onKeyDown={onKeyDown}>
			<TextField data-cy="cf-text-input" label={def.displayName} value={draft} onValueChange={setDraft} />
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="cf-cancel" onPress={close}>
					{labels.cancel}
				</Button>
				<Button emphasis={EMPHASIS.HIGH} data-cy="cf-submit" disabled={unchanged} onPress={save}>
					{labels.update}
				</Button>
			</Actions>
		</Body>
	);
};

export default TextFieldEditor;
